import React, { useEffect, useState } from 'react';
import { absenceAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

function computeDays(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
}

export default function AbsenceModal({ open, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    note: '',
  });

  useEffect(() => {
    if (!open) return;
    setLoadingTypes(true);
    absenceAPI.types()
      .then(({ data }) => setTypes(data.leave_types || []))
      .catch(() => toast.error('Failed to load leave types'))
      .finally(() => setLoadingTypes(false));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setForm({ leave_type_id: '', start_date: '', end_date: '', note: '' });
    }
  }, [open]);

  const days = computeDays(form.start_date, form.end_date);

  const handleSubmit = async () => {
    if (!form.leave_type_id) return toast.error('Select a leave type');
    if (!form.start_date || !form.end_date) return toast.error('Pick start and end dates');
    if (days <= 0) return toast.error('End date must be on or after start date');

    setSubmitting(true);
    try {
      await absenceAPI.create({ ...form, days });
      toast.success('Time off request submitted');
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-md" data-testid="absence-modal">
        <DialogHeader>
          <DialogTitle>Request Time Off</DialogTitle>
          <DialogDescription>
            Submit a time-off request. Your manager will review and approve or reject it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label htmlFor="leave-type">Leave Type</Label>
            <Select
              value={form.leave_type_id}
              onValueChange={(v) => setForm({ ...form, leave_type_id: v })}
            >
              <SelectTrigger id="leave-type" data-testid="absence-type-trigger">
                <SelectValue placeholder={loadingTypes ? 'Loading…' : 'Select leave type'} />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`absence-type-${t.id}`}>
                    {t.name}{t.paid ? ' (paid)' : ' (unpaid)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="absence-start-date"
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                data-testid="absence-end-date"
              />
            </div>
          </div>

          {days > 0 && (
            <p className="text-xs text-slate-500">
              Total: <strong>{days} day{days !== 1 ? 's' : ''}</strong>
            </p>
          )}

          <div>
            <Label htmlFor="note">Reason (optional)</Label>
            <Textarea
              id="note"
              placeholder="Reason for time off"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="absence-note"
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="absence-cancel-btn">
            Cancel
          </Button>
          <Button
            className="bg-orange-500 hover:bg-orange-600 text-white"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="absence-submit-btn"
          >
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting…</> : 'Submit Request'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
